import { database } from '../firebase'

// Live "is typing" presence over RTDB, the same protocol the registration app
// (ydo-ads-login utils/typingStatus.js) and yourdoctors-os typing-status.ts use,
// so the doctor portal and every patient chat see each other:
//   - heartbeat: Date.now() (a number) at /typingStatus/{chatId}/{uid}, written at
//     most once per TYPING_HEARTBEAT_MS while typing;
//   - a heartbeat younger than TYPING_TTL_MS means "typing"; the listener clears
//     itself when it goes stale, so a closed tab never leaves a stuck indicator;
//   - the patient also writes a draft preview at /typingPreview/{chatId}/{uid} =
//     { text, at }. The clinician never writes a preview: the patient must not see
//     what is being typed.
// The database rules allow each user to write only their own {uid} entry, with a
// number at /typingStatus and { text, at } at /typingPreview. Typing lives on its
// own top-level nodes, not inside /openconversations, so a keystroke does not fire
// openConversationsOnUpdate or resend the chat to every chat-list listener.
// Every write is best-effort: a failure only means a missing indicator.

export const TYPING_HEARTBEAT_MS = 2500
export const TYPING_TTL_MS = 6000

export const typingPath = (chatId, uid) => `/typingStatus/${chatId}/${uid}`
export const previewPath = (chatId, uid) => `/typingPreview/${chatId}/${uid}`

const writeNode = (path, value) => {
  try {
    const p = database.ref(path).set(value)
    if (p && typeof p.catch === 'function') p.catch(() => {})
  } catch (e) {
    // best-effort
  }
}

// Heartbeat-only publisher (the clinician's side). ping() on every keystroke with
// a non-empty draft; stop() on send, empty input, blur and unmount.
export function createTypingPublisher(chatId, uid) {
  let lastWrite = 0
  let idleTimer = null
  const clearIdle = () => {
    if (idleTimer) {
      clearTimeout(idleTimer)
      idleTimer = null
    }
  }
  const clearNode = () => writeNode(typingPath(chatId, uid), null)

  return {
    ping() {
      if (!chatId || !uid) return
      const now = Date.now()
      if (now - lastWrite >= TYPING_HEARTBEAT_MS) {
        lastWrite = now
        writeNode(typingPath(chatId, uid), now)
      }
      clearIdle()
      idleTimer = setTimeout(() => {
        idleTimer = null
        clearNode()
        lastWrite = 0
      }, TYPING_TTL_MS)
    },
    stop() {
      clearIdle()
      const wasTyping = lastWrite > 0
      lastWrite = 0
      if (chatId && uid && wasTyping) clearNode()
    },
  }
}

// cb(true) while otherUid has a fresh heartbeat, cb(false) when absent or stale.
// Returns an unsubscribe that also cancels the pending stale timer.
export function listenTyping(chatId, otherUid, cb) {
  if (!chatId || !otherUid) {
    cb(false)
    return () => {}
  }
  let staleTimer = null
  const clearTimer = () => {
    if (staleTimer) {
      clearTimeout(staleTimer)
      staleTimer = null
    }
  }
  let ref = null
  const onValue = (snap) => {
    clearTimer()
    const value = snap && typeof snap.val === 'function' ? snap.val() : null
    const ts = typeof value === 'number' ? value : 0
    const age = Date.now() - ts
    if (ts > 0 && age < TYPING_TTL_MS) {
      cb(true)
      staleTimer = setTimeout(() => {
        staleTimer = null
        cb(false)
      }, TYPING_TTL_MS - age)
    } else {
      cb(false)
    }
  }
  const onError = () => {
    clearTimer()
    cb(false)
  }
  try {
    ref = database.ref(typingPath(chatId, otherUid))
    ref.on('value', onValue, onError)
  } catch (e) {
    cb(false)
  }
  return () => {
    clearTimer()
    try {
      if (ref) ref.off('value', onValue)
    } catch (e) {
      // ignore
    }
  }
}

// cb(text) with the other side's current draft preview ('' when there is none).
export function listenPreview(chatId, otherUid, cb) {
  if (!chatId || !otherUid) {
    cb('')
    return () => {}
  }
  let ref = null
  const onValue = (snap) => {
    const value = snap && typeof snap.val === 'function' ? snap.val() : null
    cb(value && typeof value.text === 'string' ? value.text : '')
  }
  try {
    ref = database.ref(previewPath(chatId, otherUid))
    ref.on('value', onValue, () => cb(''))
  } catch (e) {
    cb('')
  }
  return () => {
    try {
      if (ref) ref.off('value', onValue)
    } catch (e) {
      // ignore
    }
  }
}
